import moment from 'moment';
import { FC, useCallback, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { PiCalendar, PiUser } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import StatusDropdown from '../../../../../components/forms/StatusDropdown';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import useFetch from '../../../../../hooks/useFetch';
import Page from '../../../../../layout/Page/Page';
import { PickupStatusLogService } from '../../../../../services/pickup/pickupStatusLogService';
import { PickupStatusLogApiResponse } from '../../../../../type/entities/pickup-status-log-type';
import { pickupStatuses } from '../../../../../utils/SelectorMapping';
import PickupStatusHistoryFilters from './PickupStatusHistoryFilters';

const columnOrder = [
    'id',
    'previousStatus',
    'newStatus',
    'changeReason',
    'changedBy',
    'changedAt',
    'createdAt'
];

const PickupStatusHistory: FC = () => {

    // STATES
    const [openFilters, setOpenFilters] = useState(false);

    // HOOKS
    const { userCan } = usePrivilege();
    const { id: pickupId = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new PickupStatusLogService();

    // DATA FETCHING

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @EN FETCH PICKUP STATUS LOGS BY PICKUP ID
     * @ES OBTIENE LOS LOGS DE ESTADO DE UNA RECOGIDA POR SU ID
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, refetch] = useFetch(useCallback(async () => {
        if (!pickupId) return;
        const filtersWithPickup = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                pickup: pickupId
            }
        };
        const response = await service.listPickupStatusLogs(filtersWithPickup);
        return response.getResponseData() as PickupStatusLogApiResponse;
    }, [filters, pickupId]));
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------

    // RENDER
    return (
        <Page container='fluid'>
            {/* Filtros de estados con Buttons */}
            {data !== undefined && (
                <CustomTable
                    id={'pickup-status-history-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['arrow']}
                    columnsNotShown={['createdAt']}
                    overrideColumns={[
                        {
                            key: 'previousStatus',
                            columnNameKey: 'Estado Anterior',
                            render: (row: any) => {
                                const status = row.row.original?.previousStatus;
                                const statusOption = pickupStatuses.find((item) => item.value === status);
                                if (!status) return 'Sin estado';
                                return (
                                    <div className='flex flex-row gap-2 justify-content-between'>
                                        <div className='flex flex-1 flex-col gap-2 justify-content-center text-center pointer-events-none' key={row.row.original.id + status}>
                                            <StatusDropdown
                                                entityId={row.row.original?.id}
                                                title="¿Estas seguro de cambiar el estado?"
                                                statesOptions={pickupStatuses}
                                                currentState={{
                                                    name: statusOption?.label,
                                                    id: statusOption?.value,
                                                    color: statusOption?.color
                                                }}
                                                handleStateChange={(entityId: string, status: any, comment: string) => console.log(entityId, status, comment)}
                                                disabled={!userCan('edit', 'vehicle') || status === 'sold'}
                                            />
                                        </div>
                                        <div className='flex justify-center items-center ms-3'>
                                            <FaArrowRight className='text-2xl text-gray-500' />
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'newStatus',
                            columnNameKey: 'Nuevo Estado',
                            render: (row: any) => {
                                const status = row.row.original?.newStatus;
                                const statusOption = pickupStatuses.find((item) => item.value === status);
                                if (!status) return 'Sin estado';
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center pointer-events-none' key={row.row.original.id + status}>
                                        <StatusDropdown
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado?"
                                            statesOptions={pickupStatuses}
                                            currentState={{
                                                name: statusOption?.label,
                                                id: statusOption?.value,
                                                color: statusOption?.color
                                            }}
                                            handleStateChange={(entityId: string, status: any, comment: string) => console.log(entityId, status, comment)}
                                            disabled={!userCan('edit', 'vehicle') || status === 'sold'}
                                        />
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'changeReason',
                            columnNameKey: 'Motivo del Cambio',
                            render: (row: any) => {
                                const reason = row.row.original?.changeReason;
                                return (
                                    <span className="text-sm text-gray-900">
                                        {reason || 'Sin especificar'}
                                    </span>
                                );
                            }
                        },
                        {
                            key: 'changedBy',
                            columnNameKey: 'Usuario',
                            render: (row: any) => {
                                const user = row.row.original?.changedBy;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <PiUser className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <div className="flex flex-col">
                                            <span className="font-medium">
                                                {user ? `${user.name} ${user.lastName}` : 'Sin usuario'}
                                            </span>
                                            {user?.email && (
                                                <span className="text-xs text-gray-500">{user.email}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'changedAt',
                            columnNameKey: 'Fecha de Cambio',
                            render: (row: any) => {
                                const date = row.row.original?.changedAt?.date;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <PiCalendar className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <span className="font-medium">
                                            {date ? moment(date).format('DD/MM/YYYY HH:mm') : 'Sin fecha'}
                                        </span>
                                    </div>
                                );
                            }
                        }
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as PickupStatusLogApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView
                isOpen={openFilters}
                setOpenDrawer={setOpenFilters}
                drawerTitle="Filtros de Historial de Estados"
                onClear={() => resetFilters(50)}
            >
                <PickupStatusHistoryFilters
                    filters={filters}
                    updateFilters={updateFilters}
                />
            </FilterDrawerView>
        </Page>
    );
};

export default PickupStatusHistory;